/**
 * ============================================================
 * Projects.js — Public Projects Directory Page
 * ============================================================
 *
 * Loads all projects (GET /api/projects)
 * Groups:
 *   -> Current projects (past = 0)
 *   -> Past projects grouped by endyear DESC
 *
 * Backend returns:
 *   [
 *     { id, name, shortname, startyear, endyear, past, ... },
 *     ...
 *   ]
 **/


import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";

import SynlabNavbar from "../components/SynlabNavbar";
import SynlabFooter from "../components/SynlabFooter";
import ProjectCard from "../components/ProjectCard";
import ProjectCardCompact from "../components/ProjectCardCompact";

import "./css/Page.css";     // global page layout
import "./css/Subpage.css";  // shared layout for subpages


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function Projects() {
  const [currentProjects, setCurrentProjects] = useState([]);
  const [pastProjects, setPastProjects] = useState([]);
  const [years, setYears] = useState([]);

  const [showMoreYears, setShowMoreYears] = useState(false);
  const YEAR_LIMIT = 3;

  // ----------------------------------------------------------
  // Load All Projects
  // ----------------------------------------------------------
  useEffect(() => {
    async function load() {
      const data = await fetch("/api/projects").then(res => res.json());

      const curr = data.filter(p => !p.past);
      const past = data.filter(p => p.past);

      // Collect unique end years for past projects
      const endYears = [...new Set(past.map(p => p.endyear))]
        .filter(y => y && y !== 0)
        .sort((a, b) => b - a);   // newest → oldest

      setCurrentProjects(curr);
      setPastProjects(past);
      setYears(endYears);
    }

    load();
  }, []);


  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
 return (
    <div className="page-wrapper">

      {/* ----- NAVBAR ----- */}
      <header className="header">
        <SynlabNavbar />
      </header>

      {/* ----- FLEX COLUMN WRAPPER ----- */}
      <div className="inner-page-wrapper">

        {/* ----- HEADER IMAGE + TITLE OVERLAY ----- */}
        <div className="header-image-container">
          <img
            src="/headers/Header_projects.png"
            alt="Projects Header"
            className="header-image"
          />
          <h1 className="header-title">Projects</h1>
        </div>

        {/* ----- MAIN CONTENT ----- */}
        <section className="page-content">
          <Container className="page-inner" fluid="lg">

            {/* ---------------- CURRENT PROJECTS ---------------- */}
            {currentProjects.length > 0 && (
              <>
                <h2 className="page-header">Current Projects</h2>

                <div className="page-container">
                  {currentProjects.map(project => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
              </>
            )}

            {/* ---------------- PAST PROJECTS ---------------- */}
            {pastProjects.length > 0 && (
              <>
                <h2 className="page-header">Past Projects</h2>

                {/* ----- YEAR GROUPS ----- */}
                {years.map((year, index) => {
                  if (!showMoreYears && index > YEAR_LIMIT) return null;

                  return (
                    <div key={year}>
                      <h3 className="page-subheader">{year}</h3>

                      <div className="page-container">
                        {pastProjects
                          .filter(p => p.endyear === year)
                          .map(project => (
                            <ProjectCardCompact key={project.id} project={project} />
                          ))}
                      </div>
                    </div>
                  );
                })}

                {/* ----- Show More Years Button ----- */}
                {years.length > YEAR_LIMIT && (
                  <button
                    className="show-more-button"
                    onClick={() => setShowMoreYears(!showMoreYears)}
                  >
                    {showMoreYears ? "Show Less" : "Show More"}
                  </button>
                )}
              </>
            )}

          </Container>
        </section>
      </div>

      {/* ----- FOOTER ----- */}
      <SynlabFooter />
    </div>
  );
}

export default Projects;